// The trader's chart drawings, per coin: saved in this browser
// (localStorage), shared by every chart showing the coin, with undo / redo.
import { useCallback, useSyncExternalStore } from "react";
import { DRAWING_TYPES, type Drawing, type DrawingChange } from "./types";

const KEY_PREFIX = "vp.chart.drawings.";
const HISTORY_LIMIT = 50;
const TYPES = new Set<string>(DRAWING_TYPES);

interface CoinState {
  drawings: Drawing[];
  past: Drawing[][];
  future: Drawing[][];
}

const EMPTY: CoinState = { drawings: [], past: [], future: [] };
const states = new Map<string, CoinState>();
const listeners = new Map<string, Set<() => void>>();

/** Keeps only well-formed drawings (the saved JSON may be old or edited). */
export function parseDrawings(raw: string | null): Drawing[] {
  if (!raw) return [];
  try {
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.filter(
      (d): d is Drawing =>
        typeof d === "object" &&
        d !== null &&
        typeof d.id === "string" &&
        TYPES.has(d.type) &&
        typeof d.color === "string" &&
        Number.isFinite(d.price),
    );
  } catch {
    return [];
  }
}

/** A change applied to a list: updates replace by id, removals drop. */
export function applyChange(list: Drawing[], change: DrawingChange): Drawing[] {
  const removed = new Set(change.removed ?? []);
  const updated = new Map((change.updated ?? []).map(d => [d.id, d]));
  const next = list.filter(d => !removed.has(d.id)).map(d => updated.get(d.id) ?? d);
  return [...next, ...(change.added ?? []).filter(d => !removed.has(d.id))];
}

function read(symbol: string): CoinState {
  let s = states.get(symbol);
  if (!s) {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(KEY_PREFIX + symbol);
    } catch {
      /* storage blocked: start empty */
    }
    s = { ...EMPTY, drawings: parseDrawings(raw) };
    states.set(symbol, s);
  }
  return s;
}

function write(symbol: string, next: CoinState): void {
  states.set(symbol, next);
  try {
    if (next.drawings.length === 0) localStorage.removeItem(KEY_PREFIX + symbol);
    else localStorage.setItem(KEY_PREFIX + symbol, JSON.stringify(next.drawings));
  } catch {
    /* storage full or blocked: keep them for this session */
  }
  for (const fn of listeners.get(symbol) ?? []) fn();
}

function commit(symbol: string, drawings: Drawing[]): void {
  const s = read(symbol);
  if (drawings === s.drawings) return;
  write(symbol, { drawings, past: [...s.past, s.drawings].slice(-HISTORY_LIMIT), future: [] });
}

export const drawingStore = {
  get: (symbol: string): CoinState => read(symbol),

  change(symbol: string, change: DrawingChange): void {
    commit(symbol, applyChange(read(symbol).drawings, change));
  },

  /** Patch one drawing. `record: false` folds it into the last undo step
   *  (the rest of a typing session). */
  update(symbol: string, id: string, patch: Partial<Drawing>, record = true): void {
    const s = read(symbol);
    const d = s.drawings.find(x => x.id === id);
    if (!d) return;
    const drawings = applyChange(s.drawings, { updated: [{ ...d, ...patch, id }] });
    if (record) commit(symbol, drawings);
    else write(symbol, { ...s, drawings });
  },

  clear(symbol: string): void {
    if (read(symbol).drawings.length > 0) commit(symbol, []);
  },

  undo(symbol: string): void {
    const s = read(symbol);
    const prev = s.past.at(-1);
    if (!prev) return;
    write(symbol, { drawings: prev, past: s.past.slice(0, -1), future: [s.drawings, ...s.future] });
  },

  redo(symbol: string): void {
    const s = read(symbol);
    const next = s.future[0];
    if (!next) return;
    write(symbol, { drawings: next, past: [...s.past, s.drawings], future: s.future.slice(1) });
  },

  subscribe(symbol: string, fn: () => void): () => void {
    let set = listeners.get(symbol);
    if (!set) listeners.set(symbol, (set = new Set()));
    set.add(fn);
    return () => set.delete(fn);
  },

  /** Tests only. */
  reset(): void {
    states.clear();
    listeners.clear();
  },
};

/** The coin's drawings and the actions on them. */
export function useDrawings(symbol: string) {
  const subscribe = useCallback((fn: () => void) => drawingStore.subscribe(symbol, fn), [symbol]);
  const state = useSyncExternalStore(subscribe, () => drawingStore.get(symbol));
  return {
    drawings: state.drawings,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}
